<template>
  <!-- Phase 6: polish parity with payments (empty/header/card/more). -->
  <div class="cilm" :class="{ 'cilm--selecting': selectionMode }" dir="rtl">
    <div class="cilm__header">
      <div class="cilm__bar">
        <div class="cilm__bar-actions">
          <button
            type="button"
            class="cilm__bar-btn cilm__bar-btn--create"
            :aria-label="$t(costList.typeForm)"
            @click="goToNew"
          >
            <v-icon size="22" color="#5B7C99">mdi-plus</v-icon>
          </button>
          <button
            type="button"
            class="cilm__bar-btn"
            :aria-label="$t('refresh')"
            @click="onRefreshTap"
          >
            <v-icon size="20" color="#37474F">mdi-refresh</v-icon>
          </button>
        </div>
        <h1 class="cilm__title">{{ $t($route.name) }}</h1>
        <button
          type="button"
          class="cilm__bar-btn cilm__bar-btn--back"
          :aria-label="$t('back')"
          @click="goBack"
        >
          <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
        </button>
      </div>

      <div class="cilm__search-row">
        <div class="cilm__search">
          <v-icon size="20" color="#90A4AE" class="cilm__search-icon">
            mdi-magnify
          </v-icon>
          <input
            v-model="costList.search"
            type="search"
            class="cilm__search-input"
            :placeholder="$t('search')"
            autocomplete="off"
          />
          <button
            v-if="costList.search"
            type="button"
            class="cilm__search-clear"
            :aria-label="$t('clear')"
            @click="costList.search = ''"
          >
            <v-icon size="16" color="#90A4AE">mdi-close</v-icon>
          </button>
        </div>
        <button
          type="button"
          class="cilm__filter-btn"
          :class="{ 'cilm__filter-btn--active': hasAnyFilter }"
          :aria-label="$t('filter')"
          @click="onFilterTap"
        >
          <v-icon
            size="22"
            :color="hasAnyFilter ? '#1565C0' : '#546E7A'"
          >
            mdi-filter-outline
          </v-icon>
        </button>
        <button
          type="button"
          class="cilm__filter-btn"
          :class="{ 'cilm__filter-btn--active': selectionMode }"
          :aria-label="$t('select')"
          @click="toggleSelectionMode"
        >
          <v-icon size="22" :color="selectionMode ? '#1565C0' : '#546E7A'">
            mdi-checkbox-multiple-marked-outline
          </v-icon>
        </button>
      </div>

      <div v-if="selectionMode" class="cilm__select-bar" dir="rtl">
        <button
          type="button"
          class="cilm__select-bar-btn"
          @click="selectAllLoaded"
        >
          <v-icon size="17" color="#5B7C99">mdi-check-all</v-icon>
          {{ $t("selectAllItems") }}
        </button>
        <span class="cilm__select-count">
          {{ selectedItems.length }} {{ $t("selectedCount") }}
        </span>
        <button
          type="button"
          class="cilm__select-bar-btn"
          @click="clearSelection"
        >
          <v-icon size="17" color="#90A4AE">mdi-close</v-icon>
          {{ $t("deselectAll") }}
        </button>
      </div>
    </div>

    <div v-if="costList.loading && !displayItems.length" class="cilm__state">
      <v-progress-circular indeterminate color="#5B7C99" size="28" />
    </div>
    <div v-else-if="!displayItems.length" class="cilm__state cilm__state--empty">
      <img
        class="cilm__empty-logo"
        src="@/assets/dashboard/no-data-cloud.png"
        alt=""
      />
      <p class="cilm__empty-text">{{ $t("no-data") }}</p>
    </div>
    <div v-else class="cilm__list">
      <article
        v-for="(item, index) in displayItems"
        :key="item.id || index"
        class="cilm-card"
        :class="{
          'cilm-card--selected': selectionMode && isSelected(item),
        }"
      >
        <div
          v-if="selectionMode"
          class="cilm-card__select-overlay"
          :class="{ 'cilm-card__select-overlay--on': isSelected(item) }"
          @click="toggleSelect(item)"
        >
          <span v-if="isSelected(item)" class="cilm-card__check">
            <v-icon size="17" color="#FFFFFF">mdi-check</v-icon>
          </span>
        </div>
        <div class="cilm-card__head">
          <div class="cilm-card__title-block">
            <button
              type="button"
              class="cilm-card__name-link"
              :disabled="!!item.lock"
              @click="costList.edit(item)"
            >
              <span class="cilm-card__name">
                {{ item.account && item.account.name ? item.account.name : "—" }}
              </span>
              <v-icon
                v-if="item.lock"
                size="14"
                color="#E53935"
                class="ms-1"
              >
                mdi-lock-outline
              </v-icon>
              <v-icon v-else size="12" color="#879EB6">mdi-pencil-outline</v-icon>
            </button>
            <div class="cilm-card__sub">
              {{ $t("date") }}: {{ item.date || "—" }}
            </div>
          </div>
          <span class="cilm-card__number">#{{ item.id }}</span>
        </div>

        <div class="cilm-card__amounts">
          <div class="cilm-card__amount-row">
            <span class="cilm-card__amount-label">{{ $t("price") }}</span>
            <span
              class="cilm-card__amount-value"
              :class="{
                'cilm-card__amount-value--cost': item.type === 'cost',
                'cilm-card__amount-value--income': item.type === 'income',
              }"
            >
              {{ amountText(item) }}
            </span>
          </div>
          <div class="cilm-card__amount-row">
            <span class="cilm-card__amount-label">{{ $t("account") }}</span>
            <span class="cilm-card__amount-value">
              {{
                item.customer && item.customer.name ? item.customer.name : "—"
              }}
            </span>
          </div>
          <div class="cilm-card__amount-row">
            <span class="cilm-card__amount-label">
              {{ $t("bank") }}/{{ $t("cash") }}
            </span>
            <span class="cilm-card__amount-value">
              {{ item.bank && item.bank.name ? item.bank.name : "—" }}
            </span>
          </div>
          <div class="cilm-card__amount-row">
            <span class="cilm-card__amount-label">{{ $t("document-number") }}</span>
            <span class="cilm-card__amount-value">
              {{
                item.doc_header && item.doc_header.number
                  ? item.doc_header.number
                  : "—"
              }}
            </span>
          </div>
        </div>

        <div v-if="!selectionMode" class="cilm-card__footer">
          <div class="cilm-card__actions">
            <button
              v-show="removePermission(costList.typeForm)"
              type="button"
              class="cilm-btn cilm-btn--danger"
              :disabled="!!item.lock"
              @click="askDelete(item)"
            >
              <v-icon size="14" color="#E53935">mdi-trash-can-outline</v-icon>
              {{ $t("remove") }}
            </button>
            <button
              type="button"
              class="cilm-btn cilm-btn--primary"
              :disabled="!!item.lock"
              @click="costList.edit(item)"
            >
              <v-icon size="14" color="#FFFFFF">mdi-pencil-outline</v-icon>
              {{ $t("edit") }}
            </button>
            <button
              type="button"
              class="cilm-btn cilm-btn--outline"
              @click="openDetails(item)"
            >
              <v-icon size="14" color="#5B7C99">mdi-eye-outline</v-icon>
              {{ $t("details") }}
            </button>
          </div>
          <button
            type="button"
            class="cilm-rel-btn"
            :disabled="!hasRelation(item)"
            @click="openRelationsSheet(item)"
          >
            <v-icon
              size="14"
              :color="hasRelation(item) ? '#5B7C99' : '#90A4AE'"
            >
              mdi-link-variant
            </v-icon>
            {{ $t("relations") }}
          </button>
        </div>
      </article>
      <div v-if="appending" class="cilm__state cilm__state--more">
        <v-progress-circular indeterminate color="#5B7C99" size="22" />
      </div>
    </div>

    <div ref="sentinel" class="cilm__sentinel" />

    <div v-if="selectionMode" class="cilm-bulkbar" dir="rtl">
      <button
        v-if="lockPermission"
        type="button"
        class="cilm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkLock('lock')"
      >
        <v-icon size="19" color="#5B7C99">mdi-lock-outline</v-icon>
        {{ $t("lock") }}
      </button>
      <button
        v-if="lockPermission"
        type="button"
        class="cilm-bulkbar__btn"
        :disabled="!selectedItems.length"
        @click="bulkLock('unlock')"
      >
        <v-icon size="19" color="#5B7C99">mdi-lock-open-outline</v-icon>
        {{ $t("unlock") }}
      </button>
      <button
        v-if="removePermission(costList.typeForm)"
        type="button"
        class="cilm-bulkbar__btn cilm-bulkbar__btn--danger"
        :disabled="!selectedItems.length"
        @click="askGroupDelete"
      >
        <v-icon size="19" color="#E53935">mdi-trash-can-outline</v-icon>
        {{ $t("groupDelete") }}
      </button>
    </div>

    <v-bottom-sheet v-model="detailSheet" scrollable>
      <v-card class="cilm-sheet">
        <div class="cilm-sheet__handle" />
        <div class="cilm-sheet__header" dir="rtl">
          <div class="cilm-sheet__header-side">
            <span v-if="detailItem" class="cilm-sheet__num-chip">
              #{{ detailItem.id }}
            </span>
          </div>
          <div class="cilm-sheet__title">{{ $t("details") }}</div>
          <div class="cilm-sheet__header-side cilm-sheet__header-side--end">
            <button
              type="button"
              class="cilm-sheet__back"
              :aria-label="$t('back')"
              @click="detailSheet = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
        </div>

        <div v-if="detailItem" class="cilm-sheet__quickbar" dir="rtl">
          <button
            type="button"
            class="cilm-sheet__quick"
            :disabled="!!detailItem.lock"
            @click="runNamedAction('edit')"
          >
            <v-icon size="18" color="#5B7C99">mdi-pencil-outline</v-icon>
            {{ $t("edit") }}
          </button>
          <button
            v-if="lockPermission"
            type="button"
            class="cilm-sheet__quick"
            @click="runNamedAction('lock')"
          >
            <v-icon
              size="18"
              :color="detailItem.lock ? 'green' : 'pink'"
            >
              {{
                detailItem.lock
                  ? "mdi-lock-open-outline"
                  : "mdi-lock-outline"
              }}
            </v-icon>
            {{ detailItem.lock ? $t("unlock") : $t("lock") }}
          </button>
          <button
            type="button"
            class="cilm-sheet__quick"
            v-clipboard:copy="copyLink(detailItem)"
            @click="onCopyLinkQuick"
          >
            <v-icon size="18" color="#5B7C99">mdi-link</v-icon>
            {{ $t("copy-link") }}
          </button>
          <button
            type="button"
            class="cilm-sheet__quick"
            @click="moreSheet = true"
          >
            <v-icon size="18" color="#5B7C99">mdi-dots-horizontal</v-icon>
            {{ $t("more") }}
          </button>
        </div>

        <div v-if="detailItem" class="cilm-sheet__name-block" dir="rtl">
          <div class="cilm-sheet__name-row">
            <span class="cilm-sheet__row-label">
              {{ $t(costList.typeForm) }}
            </span>
            <span class="cilm-sheet__name-value">
              {{
                detailItem.account && detailItem.account.name
                  ? detailItem.account.name
                  : "—"
              }}
            </span>
          </div>
        </div>

        <div v-if="detailItem" class="cilm-sheet__body" dir="rtl">
          <div class="cilm-sheet__card">
            <div
              v-for="(row, i) in detailRows(detailItem)"
              :key="'detail-row-' + i"
              class="cilm-sheet__row"
              :class="{
                'cilm-sheet__row--band':
                  row.kind === 'band' ||
                  row.kind === 'amount' ||
                  row.kind === 'money',
                'cilm-sheet__row--cost':
                  row.kind === 'amount' && row.tone === 'cost',
                'cilm-sheet__row--income':
                  row.kind === 'amount' && row.tone === 'income',
              }"
            >
              <span class="cilm-sheet__row-label">{{ row.label }}</span>
              <span class="cilm-sheet__row-value">{{ row.value }}</span>
            </div>
            <div class="cilm-sheet__row">
              <span class="cilm-sheet__row-label">{{ $t("relation-whit") }}</span>
              <span
                v-if="hasRelation(detailItem)"
                class="cilm-sheet__tags"
              >
                <v-chip
                  v-if="detailItem.ref_name"
                  label
                  outlined
                  small
                  class="px-1"
                  @click="goRelation(detailItem)"
                >
                  {{ detailItem.ref_name }}
                </v-chip>
                <v-chip
                  v-if="detailItem.ref_item_name"
                  label
                  outlined
                  small
                  color="primary"
                  class="px-1"
                >
                  {{ $t("product") }} {{ detailItem.ref_item_name }}
                </v-chip>
                <v-chip
                  v-for="(integrate, ii) in detailItem.integrator || []"
                  :key="'detail-int-' + ii"
                  color="green"
                  label
                  small
                  class="px-1"
                >
                  <span class="px-1 white--text">
                    {{ $t(integrate.key) }} | {{ $t("id") }} :
                    {{ integrate.value
                    }}{{
                      integrate.number
                        ? " | " + $t("number") + ":" + integrate.number
                        : ""
                    }}
                  </span>
                </v-chip>
              </span>
              <span v-else class="cilm-sheet__row-value">—</span>
            </div>
            <div class="cilm-sheet__row">
              <span class="cilm-sheet__row-label">{{ $t("tag") }}</span>
              <span
                v-if="detailItem.tags && detailItem.tags.length"
                class="cilm-sheet__tags"
              >
                <v-chip
                  v-for="(tag, ti) in detailItem.tags"
                  :key="'detail-tag-' + ti"
                  :color="tag.color || '#607D8B'"
                  label
                  small
                  class="px-1"
                >
                  <span class="px-1 white--text">{{ tag.text }}</span>
                </v-chip>
              </span>
              <span v-else class="cilm-sheet__row-value">—</span>
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="moreSheet">
      <v-card class="cilm-sheet">
        <div class="cilm-sheet__handle" />
        <div class="cilm-sheet__header" dir="rtl">
          <span class="cilm-sheet__header-spacer" />
          <div class="cilm-sheet__title">{{ $t("more") }}</div>
          <button
            type="button"
            class="cilm-sheet__back"
            :aria-label="$t('back')"
            @click="moreSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>
        <div v-if="detailItem" class="cilm-sheet__body" dir="rtl">
          <div class="cilm-sheet__actions cilm-sheet__actions--sheet">
            <button
              v-if="showPrintAction"
              type="button"
              class="cilm-sheet__action"
              @click="runNamedAction('print')"
            >
              <v-icon size="20" color="#5B7C99">mdi-printer-outline</v-icon>
              <span>{{ $t("print") }}</span>
            </button>
            <div class="cilm-sheet__action cilm-sheet__action--log">
              <log
                :title-prop="$t('history')"
                icon-prop="mdi-history"
                :listProp="true"
                :type-prop="costList.typeForm"
                :refIdProp="detailItem.id"
                mode-prop="button"
              />
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="relationsSheet" scrollable>
      <v-card class="cilm-sheet">
        <div class="cilm-sheet__handle" />
        <div class="cilm-sheet__header" dir="rtl">
          <div class="cilm-sheet__header-side">
            <span v-if="relationsItem" class="cilm-sheet__num-chip">
              #{{ relationsItem.id }}
            </span>
          </div>
          <div class="cilm-sheet__title">{{ $t("relations") }}</div>
          <div class="cilm-sheet__header-side cilm-sheet__header-side--end">
            <button
              type="button"
              class="cilm-sheet__back"
              :aria-label="$t('back')"
              @click="relationsSheet = false"
            >
              <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
            </button>
          </div>
        </div>
        <div v-if="relationsItem" class="cilm-sheet__body" dir="rtl">
          <div v-if="!hasRelation(relationsItem)" class="cilm-rel-empty">
            —
          </div>
          <div v-else class="cilm-rel-item">
            <div v-if="relationsItem.ref_name" class="cilm-rel-item__line">
              <span class="cilm-rel-item__key">{{ $t("relation-whit") }}</span>
              <button
                type="button"
                class="cilm-rel-item__link"
                @click="goRelation(relationsItem)"
              >
                {{ relationsItem.ref_name }}
              </button>
            </div>
            <div
              v-if="relationsItem.ref_item_name"
              class="cilm-rel-item__line"
            >
              <span class="cilm-rel-item__key">{{ $t("product") }}</span>
              <span class="cilm-rel-item__val">
                {{ relationsItem.ref_item_name }}
              </span>
            </div>
            <div
              v-for="(integrate, ii) in relationsItem.integrator || []"
              :key="'rel-int-' + ii"
              class="cilm-rel-item__line"
            >
              <span class="cilm-rel-item__key">{{ $t(integrate.key) }}</span>
              <span class="cilm-rel-item__val">
                {{ integrate.value
                }}{{
                  integrate.number ? " / " + integrate.number : ""
                }}
              </span>
            </div>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>

    <!-- Layered filter: same shell fields as desktop filter dialog -->
    <v-bottom-sheet v-model="filterSheet" scrollable>
      <v-card class="cilm-sheet cilm-sheet--filter">
        <div class="cilm-sheet__handle" />
        <div class="cilm-sheet__header" dir="rtl">
          <span class="cilm-sheet__header-spacer" />
          <div class="cilm-sheet__title">{{ $t("filter") }}</div>
          <button
            type="button"
            class="cilm-sheet__back"
            :aria-label="$t('back')"
            @click="filterSheet = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="cilm-filter-nav" dir="rtl">
          <button
            v-for="group in filterGroups"
            :key="group.key"
            type="button"
            class="cilm-filter-nav__item"
            @click="openFilterGroup(group.key)"
          >
            <v-icon class="cilm-filter-nav__icon" size="22" color="#5B7C99">
              {{ group.icon }}
            </v-icon>
            <span class="cilm-filter-nav__texts">
              <span class="cilm-filter-nav__item-title">{{ group.title }}</span>
              <span class="cilm-filter-nav__item-sub">
                {{ groupSubtitle(group.key) }}
              </span>
            </span>
            <span v-if="groupHasValue(group.key)" class="cilm-filter-nav__dot" />
            <v-icon size="20" color="#90A4AE">mdi-chevron-left</v-icon>
          </button>
        </div>

        <div class="cilm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="cilm-sheet__btn cilm-sheet__btn--primary"
            @click="applyFilter"
          >
            <v-icon size="16" color="#FFFFFF">mdi-filter-outline</v-icon>
            {{ $t("filter") }}
          </button>
          <button
            type="button"
            class="cilm-sheet__btn cilm-sheet__btn--ghost"
            @click="clearFilter"
          >
            <v-icon size="16" color="#5B7C99">mdi-filter-remove-outline</v-icon>
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="filterGroupSheet" scrollable>
      <v-card class="cilm-sheet cilm-sheet--filter">
        <div class="cilm-sheet__handle" />
        <div class="cilm-sheet__header" dir="rtl">
          <span class="cilm-sheet__header-spacer" />
          <div class="cilm-sheet__title">{{ activeFilterGroupTitle }}</div>
          <button
            type="button"
            class="cilm-sheet__back"
            :aria-label="$t('back')"
            @click="backToFilterMain"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="cilm-filter-fields" dir="rtl">
          <template v-if="filterGroup === 'parties'">
            <div class="cilm-f-field-wrap">
              <div class="cilm-f-field-wrap__label">
                {{ $t(costList.typeForm) }}
              </div>
              <button
                type="button"
                class="cilm-f-picker-field"
                @click="openPicker('cmDetail')"
              >
                <span class="cilm-f-picker-field__text">
                  {{ pickerSummary("cmDetail", costList.detail) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="cilm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="cmDetail"
                  v-model="costList.detail"
                  :title="$t(costList.typeForm)"
                  type="alldetail"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
            <div class="cilm-f-field-wrap">
              <div class="cilm-f-field-wrap__label">{{ $t("account") }}</div>
              <button
                type="button"
                class="cilm-f-picker-field"
                @click="openPicker('cmAccount')"
              >
                <span class="cilm-f-picker-field__text">
                  {{ pickerSummary("cmAccount", costList.account) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="cilm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="cmAccount"
                  v-model="costList.account"
                  title="account"
                  type="person"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
            <div class="cilm-f-field-wrap">
              <div class="cilm-f-field-wrap__label">
                {{ $t("bank") }}/{{ $t("cash") }}
              </div>
              <button
                type="button"
                class="cilm-f-picker-field"
                @click="openPicker('cmBank')"
              >
                <span class="cilm-f-picker-field__text">
                  {{ pickerSummary("cmBank", costList.bank) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="cilm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="cmBank"
                  v-model="costList.bank"
                  :title="$t('bank') + '/' + $t('cash')"
                  type="bankCash"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
            <div class="cilm-f-field-wrap">
              <div class="cilm-f-field-wrap__label">{{ $t("branch") }}</div>
              <button
                type="button"
                class="cilm-f-picker-field"
                @click="openPicker('cmBranch')"
              >
                <span class="cilm-f-picker-field__text">
                  {{ pickerSummary("cmBranch", costList.branch) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="cilm-picker-host" aria-hidden="true">
                <autocomplete
                  ref="cmBranch"
                  v-model="costList.branch"
                  title="branch"
                  type="branch"
                  :filter="true"
                  :multiple="true"
                />
              </div>
            </div>
          </template>

          <template v-else-if="filterGroup === 'amount'">
            <div
              v-for="field in numpadFields"
              :key="field.key"
              class="cilm-f-field-wrap"
            >
              <div class="cilm-f-field-wrap__label">{{ field.label }}</div>
              <button
                type="button"
                class="cilm-f-picker-field"
                @click="openNumpad(field)"
              >
                <span class="cilm-f-picker-field__text">
                  {{ numpadFieldText(field.key) }}
                </span>
                <span
                  v-if="hasValue(costList[field.key])"
                  class="cilm-picker-cover__clear"
                  @click.stop="costList[field.key] = null"
                >
                  <v-icon size="18" color="#90A4AE">mdi-close</v-icon>
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </template>

          <template v-else-if="filterGroup === 'date'">
            <div
              v-for="field in dateFields"
              :key="field.key"
              class="cilm-f-field-wrap"
            >
              <div class="cilm-f-field-wrap__label">{{ field.label }}</div>
              <button
                type="button"
                class="cilm-f-picker-field"
                @click="openDateLayer(field)"
              >
                <span class="cilm-f-picker-field__text">
                  {{
                    hasValue(costList[field.key])
                      ? costList[field.key]
                      : "—"
                  }}
                </span>
                <span
                  v-if="hasValue(costList[field.key])"
                  class="cilm-picker-cover__clear"
                  @click.stop="costList[field.key] = null"
                >
                  <v-icon size="18" color="#90A4AE">mdi-close</v-icon>
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </template>

          <template v-else-if="filterGroup === 'category'">
            <div class="cilm-f-field-wrap">
              <div class="cilm-f-field-wrap__label">{{ $t("tag") }}</div>
              <button
                type="button"
                class="cilm-f-picker-field"
                @click="openPicker('cmTag')"
              >
                <span class="cilm-f-picker-field__text">
                  {{ pickerSummary("cmTag", costList.tag) }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
              <div class="cilm-picker-host" aria-hidden="true">
                <tag ref="cmTag" v-model="costList.tag" />
              </div>
            </div>
          </template>

          <template v-else-if="filterGroup === 'status'">
            <div class="cilm-f-field-wrap">
              <div class="cilm-f-field-wrap__label">
                {{ $t("lockStatusDocuments") }}
              </div>
              <button
                type="button"
                class="cilm-f-picker-field"
                @click="
                  openSelectLayer(
                    'lockStatus',
                    $t('lockStatusDocuments'),
                    costList.lockStatusItems || [],
                    'text'
                  )
                "
              >
                <span class="cilm-f-picker-field__text">
                  {{
                    selectSummary(
                      "lockStatus",
                      costList.lockStatusItems || [],
                      "text"
                    )
                  }}
                </span>
                <v-icon size="20" color="#78909C">mdi-chevron-down</v-icon>
              </button>
            </div>
          </template>
        </div>

        <div class="cilm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="cilm-sheet__btn cilm-sheet__btn--primary"
            @click="applyFilter"
          >
            <v-icon size="16" color="#FFFFFF">mdi-filter-outline</v-icon>
            {{ $t("filter") }}
          </button>
          <button
            type="button"
            class="cilm-sheet__btn cilm-sheet__btn--ghost"
            @click="backToFilterMain"
          >
            {{ $t("back") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="dateLayerOpen">
      <v-card class="cilm-sheet cilm-sheet--filter">
        <div class="cilm-sheet__handle" />
        <div class="cilm-sheet__header" dir="rtl">
          <span class="cilm-sheet__header-spacer" />
          <div class="cilm-sheet__title">{{ dateLayerLabel }}</div>
          <button
            type="button"
            class="cilm-sheet__back"
            :aria-label="$t('back')"
            @click="dateLayerOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="cilm-mobile-date" dir="rtl">
          <div class="cilm-mobile-date__selected">
            <span class="cilm-mobile-date__label">{{ dateLayerLabel }}</span>
            <strong class="cilm-mobile-date__value">
              {{
                hasValue(costList[dateLayerField])
                  ? costList[dateLayerField]
                  : "—"
              }}
            </strong>
            <button
              v-if="hasValue(costList[dateLayerField])"
              type="button"
              class="cilm-mobile-date__clear"
              @click="costList[dateLayerField] = null"
            >
              {{ $t("clear") }}
            </button>
          </div>
          <date-picker
            v-if="dateLayerField"
            :key="dateLayerField"
            v-model="costList[dateLayerField]"
            inline
            simple
            auto-submit
            type="date"
            :locale="$store.state.date_locale"
            :format="$store.state.date_format"
            :min="dateLayerMin"
            class="cilm-mobile-date__picker"
            color="#7B8BA3"
          />
        </div>

        <div class="cilm-sheet__footer" dir="rtl">
          <button
            type="button"
            class="cilm-sheet__btn cilm-sheet__btn--primary"
            @click="dateLayerOpen = false"
          >
            {{ $t("Save") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="selectLayerOpen">
      <v-card class="cilm-sheet cilm-sheet--filter">
        <div class="cilm-sheet__handle" />
        <div class="cilm-sheet__header" dir="rtl">
          <span class="cilm-sheet__header-spacer" />
          <div class="cilm-sheet__title">{{ selectLayerTitle }}</div>
          <button
            type="button"
            class="cilm-sheet__back"
            :aria-label="$t('back')"
            @click="selectLayerOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="cilm-select-layer" dir="rtl">
          <button
            v-for="item in selectLayerItems"
            :key="item.value"
            type="button"
            class="cilm-select-layer__item"
            @click="selectLayerPick(item.value)"
          >
            <span class="cilm-select-layer__text">
              {{ item[selectLayerTextKey] }}
            </span>
            <v-icon
              v-if="
                selectLayerField &&
                costList[selectLayerField] === item.value
              "
              size="20"
              color="#5B7C99"
            >
              mdi-check
            </v-icon>
          </button>
          <button
            v-if="selectLayerField && hasValue(costList[selectLayerField])"
            type="button"
            class="cilm-select-layer__clear"
            @click="selectLayerPick(null)"
          >
            {{ $t("clear") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="numpadOpen" @click:outside="numpadOpen = false">
      <v-card class="cilm-numpad" dir="rtl">
        <div class="cilm-sheet__handle" />
        <div class="cilm-numpad__header">
          <span class="cilm-numpad__header-spacer" />
          <div class="cilm-numpad__title">{{ numpadLabel }}</div>
          <button
            type="button"
            class="cilm-sheet__back cilm-numpad__back"
            :aria-label="$t('back')"
            @click="numpadOpen = false"
          >
            <v-icon size="22" color="#37474F">mdi-arrow-left</v-icon>
          </button>
        </div>

        <div class="cilm-numpad__display">
          <div class="cilm-numpad__display-label">{{ numpadLabel }}</div>
          <div class="cilm-numpad__display-value">{{ numpadDisplay }}</div>
        </div>

        <div class="cilm-numpad__pad" dir="ltr">
          <div class="cilm-numpad__digits">
            <button
              v-for="n in [7, 8, 9, 4, 5, 6, 1, 2, 3]"
              :key="'d-' + n"
              type="button"
              class="cilm-numpad__key"
              @click="numpadDigit(n)"
            >
              {{ n }}
            </button>
            <button
              type="button"
              class="cilm-numpad__key cilm-numpad__key--multi"
              @click="numpadAppend('00')"
            >
              00
            </button>
            <button
              type="button"
              class="cilm-numpad__key"
              @click="numpadDigit(0)"
            >
              0
            </button>
            <button
              type="button"
              class="cilm-numpad__key cilm-numpad__key--multi"
              @click="numpadAppend('000')"
            >
              000
            </button>
          </div>
          <div class="cilm-numpad__actions">
            <button
              type="button"
              class="cilm-numpad__key cilm-numpad__key--action"
              @click="numpadStep(1)"
            >
              +
            </button>
            <button
              type="button"
              class="cilm-numpad__key cilm-numpad__key--action"
              @click="numpadStep(-1)"
            >
              -
            </button>
            <button
              type="button"
              class="cilm-numpad__key cilm-numpad__key--action"
              @click="numpadClear"
            >
              C
            </button>
            <button
              type="button"
              class="cilm-numpad__key cilm-numpad__key--action"
              @click="numpadDot"
            >
              .
            </button>
            <button
              type="button"
              class="cilm-numpad__key cilm-numpad__key--action"
              @click="numpadBackspace"
            >
              <v-icon size="20" color="#5B7C99">mdi-backspace-outline</v-icon>
            </button>
          </div>
        </div>

        <div class="cilm-numpad__footer">
          <button
            type="button"
            class="cilm-numpad__confirm"
            @click="confirmNumpad"
          >
            {{ $t("confirm") }}
          </button>
        </div>
      </v-card>
    </v-bottom-sheet>

    <v-bottom-sheet v-model="deleteSheet">
      <v-card class="cilm-confirm-sheet" dir="rtl">
        <div class="cilm-confirm-sheet__handle" />
        <div class="cilm-confirm-sheet__icon">
          <v-icon size="26" color="#E53935">mdi-trash-can-outline</v-icon>
        </div>
        <div class="cilm-confirm-sheet__title">
          {{ deleteGroupMode ? $t("groupDelete") : $t("delete") }}
        </div>
        <p class="cilm-confirm-sheet__subtitle">
          {{ $t("are-you-sure-delete-this-case") }}
        </p>
        <div v-if="deleteGroupMode" class="cilm-confirm-sheet__rows">
          <div class="cilm-confirm-sheet__row">
            <span>{{ $t("totalCount") }}</span>
            <strong>{{ selectedItems.length }}</strong>
          </div>
          <div class="cilm-confirm-sheet__items">
            <div
              v-for="(sel, si) in selectedItems"
              :key="'del-item-' + si"
              class="cilm-confirm-sheet__item"
            >
              <span class="cilm-confirm-sheet__item-main">
                <span class="cilm-confirm-sheet__item-index">
                  {{ selectedRowNumber(sel) }}
                </span>
                <span class="cilm-confirm-sheet__item-name">
                  {{
                    sel.account && sel.account.name
                      ? sel.account.name
                      : sel.customer && sel.customer.name
                      ? sel.customer.name
                      : "—"
                  }}
                </span>
              </span>
              <span class="cilm-confirm-sheet__item-number">#{{ sel.id }}</span>
            </div>
          </div>
        </div>
        <div
          v-else-if="costList.deleteItem"
          class="cilm-confirm-sheet__rows"
        >
          <div class="cilm-confirm-sheet__row">
            <span>{{ $t(costList.typeForm) }}</span>
            <strong>
              {{
                costList.deleteItem.account &&
                costList.deleteItem.account.name
                  ? costList.deleteItem.account.name
                  : "—"
              }}
            </strong>
          </div>
          <div class="cilm-confirm-sheet__row">
            <span>{{ $t("account") }}</span>
            <strong>
              {{
                costList.deleteItem.customer &&
                costList.deleteItem.customer.name
                  ? costList.deleteItem.customer.name
                  : "—"
              }}
            </strong>
          </div>
          <div class="cilm-confirm-sheet__row">
            <span>
              {{ $t("number") }} {{ $t(costList.typeForm) }}
            </span>
            <strong>{{ costList.deleteItem.id }}</strong>
          </div>
          <div
            v-if="costList.deleteItem.date"
            class="cilm-confirm-sheet__row"
          >
            <span>{{ $t("date") }}</span>
            <strong>{{ costList.deleteItem.date }}</strong>
          </div>
          <div class="cilm-confirm-sheet__row">
            <span>{{ $t("price") }}</span>
            <strong>{{ amountText(costList.deleteItem) }}</strong>
          </div>
          <div
            v-if="costList.deleteItem.bank && costList.deleteItem.bank.name"
            class="cilm-confirm-sheet__row"
          >
            <span>{{ $t("bank") }} / {{ $t("cash") }}</span>
            <strong>{{ costList.deleteItem.bank.name }}</strong>
          </div>
        </div>
        <div class="cilm-confirm-sheet__actions">
          <v-btn
            color="error"
            block
            depressed
            height="48"
            class="cilm-confirm-sheet__yes"
            @click="confirmDelete"
          >
            {{ $t("yes") }}
          </v-btn>
          <v-btn
            outlined
            block
            height="48"
            color="secondary"
            class="cilm-confirm-sheet__no"
            @click="cancelDelete"
          >
            {{ $t("no") }}
          </v-btn>
        </div>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script>
export default {
  name: "CostListMobile",
  inject: ["costList"],
  components: {
    autocomplete: () =>
      import("../../../../components/general/patial/autocomplete"),
    Tag: () => import("../../../../components/general/Tag/tag"),
  },
  data() {
    return {
      displayItems: [],
      appending: false,
      observer: null,
      detailSheet: false,
      moreSheet: false,
      detailItem: null,
      relationsSheet: false,
      relationsItem: null,
      filterSheet: false,
      filterGroupSheet: false,
      filterGroup: null,
      numpadOpen: false,
      numpadField: null,
      numpadLabel: "",
      numpadDraft: "",
      numpadIsAmount: false,
      dateLayerOpen: false,
      dateLayerField: null,
      dateLayerLabel: "",
      selectLayerOpen: false,
      selectLayerField: null,
      selectLayerTitle: "",
      selectLayerItems: [],
      selectLayerTextKey: "text",
      selectionMode: false,
      selectedItems: [],
      _selectionSynced: false,
      deleteSheet: false,
      deleteGroupMode: false,
    };
  },
  computed: {
    currentPage() {
      const opts = this.costList.tableOptions || {};
      return opts.page || 1;
    },
    // Same gate as desktop row menu (prop `type`), plus typeForm for route lists.
    showPrintAction() {
      return (
        this.costList.type === "income" || this.costList.typeForm === "income"
      );
    },
    hasAnyFilter() {
      return (
        this.groupHasValue("parties") ||
        this.groupHasValue("amount") ||
        this.groupHasValue("date") ||
        this.groupHasValue("category") ||
        this.groupHasValue("status")
      );
    },
    filterGroups() {
      return [
        {
          key: "parties",
          icon: "mdi-account-outline",
          title:
            this.$t(this.costList.typeForm) +
            " / " +
            this.$t("account") +
            " / " +
            this.$t("bank"),
        },
        {
          key: "amount",
          icon: "mdi-cash",
          title: this.$t("amount"),
        },
        {
          key: "date",
          icon: "mdi-calendar",
          title: this.$t("date"),
        },
        {
          key: "category",
          icon: "mdi-shape-outline",
          title: this.$t("tag"),
        },
        {
          key: "status",
          icon: "mdi-lock-outline",
          title: this.$t("status"),
        },
      ];
    },
    activeFilterGroupTitle() {
      const group = this.filterGroups.find((g) => g.key === this.filterGroup);
      return group ? group.title : this.$t("filter");
    },
    numpadFields() {
      return [
        {
          key: "fromAmount",
          label: this.$t("from") + " " + this.$t("amount"),
          isAmount: true,
        },
        {
          key: "toAmount",
          label: this.$t("until") + " " + this.$t("amount"),
          isAmount: true,
        },
      ];
    },
    dateFields() {
      return [
        {
          key: "fromDate",
          label: this.$t("from") + " " + this.$t("date"),
        },
        {
          key: "toDate",
          label: this.$t("until") + " " + this.$t("date"),
        },
      ];
    },
    dateLayerMin() {
      if (!this.dateLayerField) return undefined;
      if (this.dateLayerField === "toDate") {
        return this.costList.fromDate || undefined;
      }
      return undefined;
    },
    numpadDisplay() {
      const raw = String(this.numpadDraft == null ? "" : this.numpadDraft);
      if (!raw) return "0";
      if (!this.numpadIsAmount) return raw;
      if (raw.endsWith(".")) {
        const base = raw.slice(0, -1) || "0";
        const formatted = this.$numberFormat
          ? this.$numberFormat(base, "plain", 0)
          : base;
        return formatted + ".";
      }
      return this.$numberFormat ? this.$numberFormat(raw, "plain", 0) : raw;
    },
  },
  watch: {
    "costList.items"(val) {
      const items = val || [];
      if (this.appending) {
        const seen = {};
        this.displayItems.forEach((i) => {
          seen[i.id] = true;
        });
        this.displayItems = this.displayItems.concat(
          items.filter((i) => !seen[i.id])
        );
        this.appending = false;
      } else {
        this.displayItems = items.slice();
      }
    },
    "costList.search"() {
      this.appending = false;
      if (this.currentPage !== 1) {
        this.costList.tableOptions = Object.assign(
          {},
          this.costList.tableOptions,
          { page: 1 }
        );
      }
    },
    "costList.selected"(val) {
      if (!this.selectionMode || !this._selectionSynced) return;
      if (!val || !val.length) {
        this._selectionSynced = false;
        this.exitSelectionMode();
      }
    },
    deleteSheet(val) {
      if (!val) this.deleteGroupMode = false;
    },
  },
  mounted() {
    this.displayItems = (this.costList.items || []).slice();
    this.costList.tableOptions = Object.assign(
      {},
      this.costList.tableOptions,
      { page: 1, itemsPerPage: 10 }
    );
    this.$nextTick(() => {
      if (!("IntersectionObserver" in window) || !this.$refs.sentinel) return;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0] && entries[0].isIntersecting) this.loadMore();
        },
        { rootMargin: "200px" }
      );
      this.observer.observe(this.$refs.sentinel);
    });
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  },
  methods: {
    goBack() {
      if (window.history.length > 1) this.$router.back();
      else this.$router.push({ name: "Home" }).catch(() => {});
    },
    goToNew() {
      const type = this.costList.typeForm;
      if (!type) return;
      if (typeof this.costList.goTo === "function") {
        this.costList.goTo(type);
      }
    },
    onRefreshTap() {
      this.appending = false;
      this.costList.tableOptions = Object.assign(
        {},
        this.costList.tableOptions,
        { page: 1 }
      );
      this.costList.getItems();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    onFilterTap() {
      this.filterSheet = true;
    },
    openPicker(refName) {
      const comp = this.$refs[refName];
      if (comp && typeof comp.openMobileDialog === "function") {
        comp.openMobileDialog();
      }
    },
    openSelectLayer(field, title, items, textKey) {
      this.selectLayerField = field;
      this.selectLayerTitle = title;
      this.selectLayerItems = items || [];
      this.selectLayerTextKey = textKey || "text";
      this.selectLayerOpen = true;
    },
    selectLayerPick(value) {
      if (this.selectLayerField) {
        this.costList[this.selectLayerField] = value;
      }
      this.selectLayerOpen = false;
    },
    selectSummary(field, items, textKey) {
      const v = this.costList[field];
      if (!this.hasValue(v)) return "—";
      const found = (items || []).find((i) => i && i.value === v);
      return found ? found[textKey] : "—";
    },
    pickerSummary(refName, value) {
      if (!this.hasValue(value)) return "—";
      const comp = this.$refs[refName];
      const items = (comp && comp.items) || [];
      const arr = Array.isArray(value) ? value : [value];
      const names = [];
      arr.forEach((v) => {
        if (v && typeof v === "object") {
          if (v.name || v.text) names.push(v.name || v.text);
        } else {
          const found = items.find((i) => i && i.id === v);
          if (found && found.name) names.push(found.name);
        }
      });
      if (names.length) return names.join(" · ");
      return String(arr.length);
    },
    openFilterGroup(key) {
      this.filterGroup = key;
      this.filterSheet = false;
      this.$nextTick(() => {
        this.filterGroupSheet = true;
      });
    },
    backToFilterMain() {
      this.filterGroupSheet = false;
      this.$nextTick(() => {
        this.filterSheet = true;
      });
    },
    hasValue(v) {
      if (Array.isArray(v)) return v.length > 0;
      return v !== null && v !== undefined && v !== "";
    },
    groupHasValue(key) {
      const list = this.costList;
      if (key === "parties") {
        return (
          this.hasValue(list.detail) ||
          this.hasValue(list.account) ||
          this.hasValue(list.bank) ||
          this.hasValue(list.branch)
        );
      }
      if (key === "amount") {
        return this.hasValue(list.fromAmount) || this.hasValue(list.toAmount);
      }
      if (key === "date") {
        return this.hasValue(list.fromDate) || this.hasValue(list.toDate);
      }
      if (key === "category") {
        return this.hasValue(list.tag);
      }
      if (key === "status") return this.hasValue(list.lockStatus);
      return false;
    },
    groupSubtitle(key) {
      const list = this.costList;
      if (key === "parties") {
        return (
          this.$t(list.typeForm) +
          " · " +
          this.$t("account") +
          " · " +
          this.$t("bank") +
          " · " +
          this.$t("branch")
        );
      }
      if (key === "amount") {
        const parts = [];
        if (this.hasValue(list.fromAmount)) {
          parts.push(
            this.$t("from") + " " + this.$t("amount") + ": " + list.fromAmount
          );
        }
        if (this.hasValue(list.toAmount)) {
          parts.push(
            this.$t("until") + " " + this.$t("amount") + ": " + list.toAmount
          );
        }
        return parts.length ? parts.join(" · ") : this.$t("amount");
      }
      if (key === "date") {
        const parts = [];
        if (this.hasValue(list.fromDate)) parts.push(list.fromDate);
        if (this.hasValue(list.toDate)) parts.push(list.toDate);
        return parts.length ? parts.join(" · ") : this.$t("date");
      }
      if (key === "category") {
        return this.$t("tag");
      }
      if (key === "status") {
        const lockItem = (list.lockStatusItems || []).find(
          (i) => i.value === list.lockStatus
        );
        return lockItem ? lockItem.text : this.$t("lockStatusDocuments");
      }
      return "";
    },
    numpadFieldText(key) {
      const value = this.costList[key];
      if (!this.hasValue(value)) return "—";
      const field = this.numpadFields.find((f) => f.key === key);
      if (field && field.isAmount && this.$numberFormat) {
        return this.$numberFormat(value, "plain", 0);
      }
      return String(value);
    },
    openNumpad(field) {
      this.numpadField = field.key;
      this.numpadLabel = field.label;
      this.numpadIsAmount = !!field.isAmount;
      const current = this.costList[field.key];
      this.numpadDraft = this.hasValue(current) ? String(current) : "";
      this.numpadOpen = true;
    },
    numpadDigit(num) {
      this.numpadAppend(String(num));
    },
    numpadAppend(chunk) {
      const piece = String(chunk);
      if (!piece) return;
      if (String(this.numpadDraft).length + piece.length > 16) return;
      if (!this.numpadDraft && /^\d+$/.test(piece)) {
        this.numpadDraft = piece.replace(/^0+(?=\d)/, "");
      } else if (this.numpadDraft === "0" && /^\d+$/.test(piece)) {
        this.numpadDraft = piece;
      } else {
        this.numpadDraft = String(this.numpadDraft) + piece;
      }
    },
    numpadDot() {
      if (!this.numpadIsAmount) return;
      if (!this.numpadDraft) {
        this.numpadDraft = "0.";
        return;
      }
      if (String(this.numpadDraft).includes(".")) return;
      this.numpadDraft = String(this.numpadDraft) + ".";
    },
    numpadBackspace() {
      const current = String(this.numpadDraft || "");
      this.numpadDraft = current.length <= 1 ? "" : current.slice(0, -1);
    },
    numpadClear() {
      this.numpadDraft = "";
    },
    numpadStep(delta) {
      const next = (parseFloat(this.numpadDraft) || 0) + delta;
      this.numpadDraft = next < 0 ? "0" : String(next);
    },
    openDateLayer(field) {
      this.dateLayerField = field.key;
      this.dateLayerLabel = field.label;
      this.dateLayerOpen = true;
    },
    confirmNumpad() {
      if (this.numpadField) {
        const raw = String(this.numpadDraft || "").trim();
        this.costList[this.numpadField] =
          raw === "" ? null : parseFloat(raw);
      }
      this.numpadField = null;
      this.numpadOpen = false;
    },
    applyFilter() {
      this.filterSheet = false;
      this.filterGroupSheet = false;
      this.appending = false;
      this.costList.tableOptions = Object.assign(
        {},
        this.costList.tableOptions,
        { page: 1 }
      );
      this.costList.getItems();
      window.scrollTo({ top: 0, behavior: "smooth" });
    },
    clearFilter() {
      const list = this.costList;
      list.detail = null;
      list.account = null;
      list.bank = null;
      list.branch = null;
      list.tag = null;
      list.fromDate = null;
      list.toDate = null;
      list.lockStatus = null;
      list.fromAmount = null;
      list.toAmount = null;
      this.applyFilter();
    },
    formatMoney(item, value) {
      const cur =
        item && item.currency && item.currency.name
          ? " " + item.currency.name
          : "";
      const n = parseFloat(value || 0);
      return (this.$numberFormat ? this.$numberFormat(n) : n) + cur;
    },
    amountText(item) {
      if (!item) return "—";
      return this.formatMoney(item, item.price);
    },
    hasRelation(item) {
      if (!item) return false;
      if (item.ref_name || item.ref_item_name) return true;
      return !!(item.integrator && item.integrator.length);
    },
    openRelationsSheet(item) {
      if (!this.hasRelation(item)) return;
      this.relationsItem = item;
      this.relationsSheet = true;
    },
    goRelation(item) {
      this.relationsSheet = false;
      this.detailSheet = false;
      if (item && typeof this.costList.relationLink === "function") {
        this.costList.relationLink(item);
      }
    },
    openDetails(item) {
      this.detailItem = item;
      this.detailSheet = true;
    },
    selectedRowNumber(item) {
      if (!item) return "";
      const idx = this.displayItems.findIndex((i) => i.id === item.id);
      return idx >= 0 ? idx + 1 : "";
    },
    askDelete(item) {
      this.moreSheet = false;
      this.detailSheet = false;
      this.deleteGroupMode = false;
      this.costList.deleteItem = item;
      this.deleteSheet = true;
    },
    askGroupDelete() {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.costList.deleteItem = null;
      this.deleteGroupMode = true;
      this.deleteSheet = true;
    },
    cancelDelete() {
      this.deleteSheet = false;
      this.deleteGroupMode = false;
    },
    confirmDelete() {
      this.deleteSheet = false;
      if (this.costList.selected && this.costList.selected.length > 0) {
        this.costList.removegroup();
      } else {
        this.costList.remove();
      }
      if (this.deleteGroupMode) {
        this.deleteGroupMode = false;
        this.exitSelectionMode();
      }
    },
    bulkLock(type) {
      if (!this.selectedItems.length) return;
      this.syncSelection();
      this.costList.lockGroup(type);
    },
    toggleSelectionMode() {
      this.selectionMode = !this.selectionMode;
      if (!this.selectionMode) {
        this.selectedItems = [];
        this._selectionSynced = false;
        this.costList.selected = [];
      }
    },
    exitSelectionMode() {
      this.selectionMode = false;
      this.selectedItems = [];
      this._selectionSynced = false;
      this.costList.selected = [];
    },
    isSelected(item) {
      return this.selectedItems.some((i) => i.id === item.id);
    },
    toggleSelect(item) {
      if (this.isSelected(item)) {
        this.selectedItems = this.selectedItems.filter(
          (i) => i.id !== item.id
        );
      } else {
        this.selectedItems = this.selectedItems.concat([item]);
      }
    },
    selectAllLoaded() {
      const seen = {};
      this.selectedItems.forEach((i) => {
        seen[i.id] = true;
      });
      this.selectedItems = this.selectedItems.concat(
        this.displayItems.filter((i) => !seen[i.id])
      );
    },
    clearSelection() {
      this.selectedItems = [];
    },
    syncSelection() {
      this.costList.selected = this.selectedItems.slice();
      this._selectionSynced = true;
    },
    copyLink(item) {
      if (!item) return "";
      const cuid = this.costList.cuid || "";
      return (
        "https://abr.abriron.com/pi/" + item.uid + "?" + "cuid=" + cuid
      );
    },
    notifyCopied() {
      if (typeof this.copied === "function") this.copied();
      else if (
        this.costList &&
        typeof this.costList.copied === "function"
      ) {
        this.costList.copied();
      }
    },
    onCopyLinkQuick() {
      this.notifyCopied();
    },
    runNamedAction(name) {
      const item = this.detailItem;
      if (!item) return;
      const list = this.costList;
      this.moreSheet = false;
      if (name === "lock") {
        this.detailSheet = false;
        list.lock(item, item.lock ? "unlock" : "lock");
        return;
      }
      if (name === "print") {
        this.detailSheet = false;
        list.print("invoiceIncomePrint", item);
        return;
      }
      this.detailSheet = false;
      if (name === "edit") list.edit(item);
    },
    detailRows(item) {
      if (!item) return [];
      const rows = [];
      const push = (label, value, kind, tone) =>
        rows.push({
          label,
          value: value == null || value === "" ? "—" : value,
          kind: kind || null,
          tone: tone || null,
        });
      const type = item.type || this.costList.typeForm;
      const amountTone =
        type === "cost" ? "cost" : type === "income" ? "income" : null;
      // Same order as desktop costList headers (coding stays in name-block).
      push(
        this.$t("number") + " " + this.$t(this.costList.typeForm),
        item.id,
        "band"
      );
      push(
        this.$t("document-number"),
        item.doc_header && item.doc_header.number,
        "band"
      );
      push(
        this.$t(this.costList.typeForm + "Type"),
        item.sub_type ? this.$t(item.sub_type) : null
      );
      push(
        this.$t("account"),
        item.customer && item.customer.name
      );
      push(
        this.$t("bank"),
        item.bank && item.bank.name,
        "band"
      );
      push(this.$t("price"), this.amountText(item), "amount", amountTone);
      push(this.$t("tax"), this.formatMoney(item, item.tax), "money");
      push(
        this.$t("discount"),
        this.formatMoney(item, item.discount),
        "money"
      );
      push(this.$t("date"), item.date);
      push(this.$t("branch"), item.branch && item.branch.name);
      push(this.$t("description"), item.description);
      if (item.user) {
        push(
          this.$t("registerar"),
          ((item.user.firstname || "") + " " + (item.user.lastname || "")).trim()
        );
      } else {
        push(this.$t("registerar"), null);
      }
      return rows;
    },
    loadMore() {
      if (!this.displayItems.length) return;
      if (this.costList.loading || this.appending) return;
      if (this.currentPage >= (this.costList.pageCount || 1)) return;
      this.appending = true;
      this.costList.tableOptions = Object.assign(
        {},
        this.costList.tableOptions,
        { page: this.currentPage + 1 }
      );
    },
  },
};
</script>

<style scoped>
.cilm {
  display: flex;
  flex-direction: column;
  padding: 0 12px 12px;
  background: #ffffff;
  min-height: calc(100vh - 52px);
  box-sizing: border-box;
  padding-bottom: calc(16px + env(safe-area-inset-bottom, 0px));
}
.cilm--selecting .cilm__list {
  padding-bottom: 88px;
}
.cilm__header {
  position: sticky;
  top: 52px;
  z-index: 4;
  background: #ffffff;
  padding: 6px 0 10px;
  border-bottom: 1px solid #eef1f4;
  margin-bottom: 10px;
}
.cilm__bar {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-bottom: 8px;
}
.cilm__bar-actions {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  min-width: 80px;
}
.cilm__bar-btn {
  flex: 0 0 40px;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  -webkit-tap-highlight-color: transparent;
}
.cilm__bar-btn:active {
  background: rgba(91, 124, 153, 0.1);
}
.cilm__bar-btn--create {
  background: rgba(91, 124, 153, 0.1);
}
.cilm__bar-btn--back {
  flex: 0 0 40px;
}
.cilm__title {
  flex: 1 1 auto;
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
  text-align: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cilm__search-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.cilm__search {
  flex: 1 1 auto;
  min-width: 0;
  height: 44px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px;
  border-radius: 10px;
  background: #f3f5f7;
  border: 1px solid #e0e4e8;
  box-sizing: border-box;
}
.cilm__search-icon {
  flex: 0 0 auto;
}
.cilm__search-input {
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: none;
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: #263238;
  text-align: right;
}
.cilm__search-input::placeholder {
  color: #90a4ae;
  font-weight: 500;
}
.cilm__search-input::-webkit-search-cancel-button {
  display: none;
}
.cilm__search-clear {
  flex: 0 0 auto;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 10px;
  background: transparent;
  cursor: pointer;
  padding: 0;
}
.cilm__filter-btn {
  flex: 0 0 44px;
  height: 44px;
  border: 1px solid #e0e4e8;
  border-radius: 10px;
  background: #ffffff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
  -webkit-tap-highlight-color: transparent;
}
.cilm__filter-btn:active {
  background: rgba(91, 124, 153, 0.1);
}
.cilm__filter-btn--active {
  background: rgba(21, 101, 192, 0.1);
  border-color: rgba(21, 101, 192, 0.35);
}
.cilm__select-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 8px;
  padding: 6px 10px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
}
.cilm__select-bar-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 6px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cilm__select-bar-btn:active {
  background: rgba(91, 124, 153, 0.12);
}
.cilm__select-count {
  font-size: 12px;
  font-weight: 700;
  color: #5b7c99;
}
.cilm__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 16px;
}
.cilm__state--empty {
  flex: 1 1 auto;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: calc(100vh - 220px);
  padding: 32px 16px 24px;
  background: #ffffff;
}
.cilm__state--more {
  padding: 14px 0;
}
.cilm__empty-logo {
  width: 72px;
  height: 72px;
  object-fit: contain;
  opacity: 0.85;
}
.cilm__empty-text {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #78909c;
  text-align: center;
}
.cilm__list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-top: 4px;
}
.cilm__sentinel {
  height: 1px;
}
.cilm-card {
  position: relative;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
  transition: transform 0.15s ease;
}
.cilm-card--selected {
  transform: scale(0.99);
  border-color: rgba(21, 101, 192, 0.45);
}
.cilm-card__select-overlay {
  position: absolute;
  inset: 0;
  z-index: 3;
  border-radius: 10px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cilm-card__select-overlay--on {
  background: rgba(38, 50, 56, 0.12);
}
.cilm-card__check {
  position: absolute;
  top: 8px;
  left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #0d47a1;
  box-shadow: 0 2px 6px rgba(13, 71, 161, 0.4);
}
.cilm-bulkbar {
  position: fixed;
  right: 12px;
  left: 12px;
  bottom: calc(16px + env(safe-area-inset-bottom, 0px));
  z-index: 6;
  display: flex;
  gap: 4px;
  padding: 8px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  box-shadow: 0 8px 24px rgba(38, 50, 56, 0.14);
}
.cilm-bulkbar__btn {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
  min-height: 48px;
  padding: 6px 2px;
  border: 0;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 10px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cilm-bulkbar__btn:active {
  background: rgba(91, 124, 153, 0.15);
}
.cilm-bulkbar__btn:disabled {
  opacity: 0.4;
  cursor: default;
}
.cilm-bulkbar__btn--danger {
  color: #e53935;
  background: rgba(229, 57, 53, 0.06);
}
.cilm-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}
.cilm-card__title-block {
  min-width: 0;
  flex: 1 1 auto;
}
.cilm-card__name-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  max-width: 100%;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cilm-card__name-link:disabled {
  cursor: default;
}
.cilm-card__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: right;
}
.cilm-card__sub {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 600;
  color: #90a4ae;
}
.cilm-card__number {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 700;
  color: #5b7c99;
}
.cilm-card__amounts {
  margin-top: 10px;
  border-top: 1px dashed rgba(144, 164, 174, 0.35);
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.cilm-card__amount-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}
.cilm-card__amount-label {
  font-size: 12px;
  color: #78909c;
}
.cilm-card__amount-value {
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
  padding: 2px 8px;
  border-radius: 8px;
}
.cilm-card__amount-value--cost {
  background: rgba(229, 57, 53, 0.1);
  color: #c62828;
}
.cilm-card__amount-value--income {
  background: rgba(67, 160, 71, 0.12);
  color: #2e7d32;
}
.cilm-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 10px;
}
.cilm-card__actions {
  display: flex;
  gap: 6px;
  flex: 1 1 auto;
  min-width: 0;
}
.cilm-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  height: 34px;
  padding: 0 10px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  border: 0;
  -webkit-tap-highlight-color: transparent;
}
.cilm-btn:disabled {
  opacity: 0.45;
  cursor: default;
}
.cilm-btn--danger {
  border: 1px solid rgba(229, 57, 53, 0.35);
  background: rgba(229, 57, 53, 0.06);
  color: #c62828;
}
.cilm-btn--primary {
  background: #5b7c99;
  color: #ffffff;
}
.cilm-btn--outline {
  background: #ffffff;
  color: #5b7c99;
  border: 1px solid #cfd8dc;
}
.cilm-rel-btn {
  flex: 0 0 auto;
  height: 34px;
  padding: 0 8px;
  border: 0;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.08);
  color: #5b7c99;
  font-size: 11px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cilm-rel-btn:disabled {
  background: transparent;
  color: #90a4ae;
  opacity: 0.55;
  cursor: default;
}
.cilm-rel-empty {
  padding: 18px 0;
  font-size: 12.5px;
  color: #90a4ae;
  text-align: center;
}
.cilm-rel-item {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 12px;
  padding: 12px 14px;
}
.cilm-rel-item__line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 28px;
  padding: 4px 0;
}
.cilm-rel-item__key {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.cilm-rel-item__val {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.cilm-rel-item__link {
  flex: 1 1 auto;
  min-width: 0;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 700;
  color: #1565c0;
  text-align: left;
  cursor: pointer;
  padding: 0;
}
.cilm-sheet {
  border-radius: 10px 10px 0 0 !important;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}
.cilm-sheet__handle {
  flex: 0 0 auto;
  width: 44px;
  height: 4px;
  border-radius: 10px;
  background: #cfd8dc;
  margin: 10px auto 2px;
}
.cilm-sheet__header {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px 10px;
  border-bottom: 1px solid #eef1f4;
}
.cilm-sheet__header-side {
  flex: 1 1 0;
  display: flex;
  align-items: center;
  min-width: 0;
}
.cilm-sheet__header-side--end {
  justify-content: flex-end;
}
.cilm-sheet__header-spacer {
  flex: 0 0 40px;
  width: 40px;
}
.cilm-sheet__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.cilm-sheet__num-chip {
  display: inline-flex;
  align-items: center;
  height: 26px;
  padding: 0 10px;
  border-radius: 10px;
  background: rgba(135, 158, 182, 0.14);
  border: 1px solid rgba(135, 158, 182, 0.28);
  color: #5b7c99;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}
.cilm-sheet__back {
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  padding: 0;
}
.cilm-sheet__back:active {
  background: rgba(91, 124, 153, 0.1);
}
.cilm-sheet__quickbar {
  flex: 0 0 auto;
  display: flex;
  gap: 6px;
  padding: 8px 12px 0;
}
.cilm-sheet__quick {
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 46px;
  padding: 4px 2px;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: rgba(91, 124, 153, 0.07);
  font-size: 9px;
  font-weight: 600;
  line-height: 1.25;
  text-align: center;
  color: #37474f;
  cursor: pointer;
  overflow: hidden;
  -webkit-tap-highlight-color: transparent;
}
.cilm-sheet__quick .v-icon {
  font-size: 18px !important;
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
}
.cilm-sheet__quick:active {
  background: rgba(91, 124, 153, 0.14);
}
.cilm-sheet__quick:disabled {
  opacity: 0.45;
  cursor: default;
}
.cilm-sheet__name-block {
  flex: 0 0 auto;
  background: #edf2f7;
  border-radius: 10px;
  padding: 10px 14px;
  margin: 6px 16px 0;
}
.cilm-sheet__name-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.cilm-sheet__name-value {
  flex: 1 1 auto;
  min-width: 0;
  max-width: 70%;
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cilm-sheet__body {
  overflow-y: auto;
  padding: 8px 16px 20px;
}
.cilm-sheet__card {
  background: #f8fafc;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  padding: 4px 14px;
}
.cilm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid #e8edf2;
}
.cilm-sheet__card .cilm-sheet__row:last-child {
  border-bottom: 0;
}
.cilm-sheet__row--band {
  margin: 0 -14px;
  padding: 8px 14px;
  border-bottom-color: rgba(91, 124, 153, 0.12);
  background: rgba(91, 124, 153, 0.05);
}
.cilm-sheet__row--cost {
  background: rgba(229, 57, 53, 0.1);
  border-bottom-color: rgba(198, 40, 40, 0.18);
}
.cilm-sheet__row--cost .cilm-sheet__row-label,
.cilm-sheet__row--cost .cilm-sheet__row-value {
  color: #c62828;
}
.cilm-sheet__row--income {
  background: rgba(67, 160, 71, 0.12);
  border-bottom-color: rgba(46, 125, 50, 0.18);
}
.cilm-sheet__row--income .cilm-sheet__row-label,
.cilm-sheet__row--income .cilm-sheet__row-value {
  color: #2e7d32;
}
.cilm-sheet__row-label {
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.cilm-sheet__row-value {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 13px;
  font-weight: 700;
  color: #37474f;
  text-align: left;
}
.cilm-sheet__tags {
  display: inline-flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
}
.cilm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.cilm-sheet__actions--sheet {
  padding-top: 4px;
}
.cilm-sheet__action {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: 42px;
  padding: 0 6px;
  border: 0;
  border-bottom: 1px solid #e8edf2;
  border-radius: 10px;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.cilm-sheet__action:active {
  background: rgba(91, 124, 153, 0.08);
}
.cilm-sheet__action--log {
  cursor: default;
  padding: 0;
}
.cilm-sheet__action--log > div {
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
}
.cilm-sheet__action--log ::v-deep .v-list-item {
  flex: 1 1 auto;
  min-height: 42px;
  height: 42px;
  padding: 0;
}
.cilm-sheet__action--log ::v-deep .v-list-item__icon {
  margin: 0 !important;
  min-width: 19px;
  align-self: center;
}
.cilm-sheet__action--log ::v-deep .v-list-item__icon .v-icon {
  font-size: 19px !important;
  color: #5b7c99 !important;
}
.cilm-sheet__action--log ::v-deep .v-list-item__title {
  margin-inline-start: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #37474f;
  text-align: right;
}
.cilm-sheet--filter {
  max-height: 92vh;
  border-radius: 20px 20px 0 0 !important;
}
.cilm-sheet__footer {
  flex: 0 0 auto;
  display: flex;
  gap: 8px;
  padding: 10px 12px calc(10px + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid #eef1f4;
  background: #ffffff;
}
.cilm-sheet__btn {
  flex: 1 1 0;
  height: 44px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cilm-sheet__btn--primary {
  border: 0;
  background: #5b7c99;
  color: #ffffff;
}
.cilm-sheet__btn--primary:active {
  background: #4d6a84;
}
.cilm-sheet__btn--ghost {
  border: 1px solid #cfd8dc;
  background: #ffffff;
  color: #546e7a;
}
.cilm-sheet__btn--ghost:active {
  background: rgba(91, 124, 153, 0.08);
}
.cilm-filter-nav {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0;
  background: #ffffff;
}
.cilm-filter-nav__item {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  gap: 12px;
  padding: 12px 16px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: #fff;
  text-align: right;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cilm-filter-nav__item:active {
  background: #f7f9fc;
}
.cilm-filter-nav__item:last-child {
  border-bottom: 0;
}
.cilm-filter-nav__icon {
  flex: 0 0 auto;
}
.cilm-filter-nav__texts {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 1 auto;
  min-width: 0;
  text-align: right;
}
.cilm-filter-nav__item-title {
  display: block;
  width: 100%;
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.35;
  text-align: right;
}
.cilm-filter-nav__item-sub {
  display: block;
  width: 100%;
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: #90a4ae;
  line-height: 1.35;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cilm-filter-nav__dot {
  flex: 0 0 auto;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #5b7c99;
}
.cilm-filter-fields {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 14px 16px 6px;
  background: #ffffff;
}
.cilm-filter-fields > * {
  margin-bottom: 14px;
}
.cilm-picker-host {
  display: none !important;
}
.cilm-picker-cover__clear {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}
.cilm-f-field-wrap {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.cilm-f-field-wrap__label {
  display: block;
  flex: 0 0 auto;
  font-size: 13px;
  font-weight: 600;
  color: #455a64;
  text-align: right;
  line-height: 1.4;
  margin: 0 2px 4px;
}
.cilm-f-picker-field {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 40px;
  min-height: 40px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid #cfd8dc;
  border-radius: 10px;
  background: #fff;
  box-sizing: border-box;
  cursor: pointer;
  text-align: right;
  -webkit-tap-highlight-color: transparent;
}
.cilm-f-picker-field:active {
  background: #eef4f8;
  border-color: #90caf9;
}
.cilm-f-picker-field__text {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  color: #263238;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.cilm-select-layer {
  padding: 6px 16px 20px;
}
.cilm-select-layer__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  min-height: 48px;
  padding: 10px 4px;
  border: 0;
  border-bottom: 1px solid #eeeeee;
  background: transparent;
  text-align: right;
  cursor: pointer;
}
.cilm-select-layer__text {
  font-size: 13.5px;
  font-weight: 500;
  color: #37474f;
}
.cilm-select-layer__clear {
  display: block;
  width: 100%;
  padding: 14px 4px 4px;
  border: 0;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #c62828;
  text-align: center;
  cursor: pointer;
}
.cilm-mobile-date {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 14px 16px;
  background: #ffffff;
}
.cilm-mobile-date__selected {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid #e0e6ed;
  border-radius: 12px;
  background: #f7f9fc;
}
.cilm-mobile-date__label {
  width: 100%;
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
}
.cilm-mobile-date__value {
  font-size: 18px;
  font-weight: 800;
  color: #263238;
  letter-spacing: 0.02em;
}
.cilm-mobile-date__clear {
  border: 0;
  background: transparent;
  color: #e91e63;
  font-size: 13px;
  font-weight: 700;
  padding: 4px 0;
  cursor: pointer;
}
.cilm-mobile-date__picker {
  width: 100%;
}
.cilm-mobile-date__picker ::v-deep .vpd-main,
.cilm-mobile-date__picker ::v-deep .vpd-wrapper,
.cilm-mobile-date__picker ::v-deep .vpd-container,
.cilm-mobile-date__picker ::v-deep .vpd-content {
  width: 100% !important;
  max-width: 100% !important;
}
.cilm-numpad {
  border-radius: 20px 20px 0 0 !important;
  background: #fff !important;
  padding-bottom: env(safe-area-inset-bottom, 0px);
}
.cilm-numpad__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 12px 6px;
}
.cilm-numpad__header-spacer {
  flex: 0 0 40px;
}
.cilm-numpad__title {
  flex: 1 1 auto;
  text-align: center;
  font-size: 15px;
  font-weight: 700;
  color: #263238;
}
.cilm-numpad__display {
  margin: 4px 16px 12px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f3f5f7;
  text-align: left;
}
.cilm-numpad__display-label {
  font-size: 12px;
  font-weight: 600;
  color: #78909c;
  text-align: right;
}
.cilm-numpad__display-value {
  margin-top: 4px;
  font-size: 28px;
  font-weight: 800;
  color: #263238;
  letter-spacing: 0.02em;
  direction: ltr;
}
.cilm-numpad__pad {
  display: flex;
  gap: 8px;
  padding: 0 12px 8px;
}
.cilm-numpad__digits {
  flex: 3 1 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
}
.cilm-numpad__actions {
  flex: 1 1 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 8px;
}
.cilm-numpad__key {
  height: 48px;
  border: 1px solid #e0e4e8;
  border-radius: 12px;
  background: #fff;
  font-size: 18px;
  font-weight: 700;
  color: #37474f;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
.cilm-numpad__key:active {
  background: #eef4f8;
}
.cilm-numpad__key--multi {
  font-size: 14px;
}
.cilm-numpad__key--action {
  background: #f7f9fc;
  color: #5b7c99;
}
.cilm-numpad__footer {
  padding: 8px 12px 12px;
}
.cilm-numpad__confirm {
  width: 100%;
  height: 48px;
  border: 0;
  border-radius: 12px;
  background: #5b7c99;
  color: #fff;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}
.cilm-numpad__confirm:active {
  background: #4d6a84;
}
.cilm-confirm-sheet {
  border-radius: 20px 20px 0 0 !important;
  padding: 8px 16px 16px;
  background: #fff !important;
}
.cilm-confirm-sheet__handle {
  width: 40px;
  height: 4px;
  margin: 4px auto 14px;
  border-radius: 999px;
  background: #cfd8dc;
}
.cilm-confirm-sheet__icon {
  width: 52px;
  height: 52px;
  margin: 0 auto 10px;
  border-radius: 50%;
  background: rgba(229, 57, 53, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
}
.cilm-confirm-sheet__title {
  text-align: center;
  font-size: 18px;
  font-weight: 800;
  color: #212121;
  margin: 0 0 8px;
}
.cilm-confirm-sheet__subtitle {
  margin: 0 0 16px;
  text-align: center;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
  line-height: 1.6;
}
.cilm-confirm-sheet__rows {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 18px;
}
.cilm-confirm-sheet__items {
  max-height: 168px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  border: 1px solid #e8edf2;
  border-radius: 10px;
  background: #f8fafc;
  padding: 4px 12px;
}
.cilm-confirm-sheet__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 7px 0;
  border-bottom: 1px solid #e8edf2;
}
.cilm-confirm-sheet__item:last-child {
  border-bottom: 0;
}
.cilm-confirm-sheet__item-main {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
}
.cilm-confirm-sheet__item-index {
  flex: 0 0 auto;
  min-width: 16px;
  font-size: 11px;
  font-weight: 700;
  color: #90a4ae;
  text-align: center;
}
.cilm-confirm-sheet__item-name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12.5px;
  font-weight: 600;
  color: #37474f;
}
.cilm-confirm-sheet__item-number {
  flex: 0 0 auto;
  font-size: 11.5px;
  font-weight: 700;
  color: #5b7c99;
}
.cilm-confirm-sheet__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 48px;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f5f7f9;
  font-size: 13px;
  font-weight: 600;
  color: #607d8b;
}
.cilm-confirm-sheet__row strong {
  font-size: 14px;
  font-weight: 800;
  color: #263238;
  text-align: left;
}
.cilm-confirm-sheet__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.cilm-confirm-sheet__yes {
  border-radius: 12px !important;
  font-weight: 800 !important;
  letter-spacing: 0 !important;
}
.cilm-confirm-sheet__no {
  border-radius: 12px !important;
  font-weight: 700 !important;
  letter-spacing: 0 !important;
}
</style>
